import * as React from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./dialog"
import { cn } from "@/lib/utils"

export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  description?: string
  /**
   * Width preset. New sizes added 2026-05-13 for surfaces that pack
   * tabular data (e.g. eUICC inventory) — `xl` ≈ 56rem, `2xl` ≈ 72rem.
   */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  footer?: React.ReactNode
  children: React.ReactNode
  className?: string
  /**
   * Override the scroll body's classes. Defaults to a single auto-scrolling
   * region. Pass a flex-column + `overflow-hidden` variant when the children
   * manage their own internal scroll (e.g. a tabbed body where each tab owns
   * one scroll region) — otherwise the body itself scrolls and any "fixed"
   * header inside it scrolls away.
   */
  bodyClassName?: string
}

const sizeClasses = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl',
  '2xl': 'sm:max-w-6xl',
}

/**
 * Modal component - A backwards-compatible wrapper around shadcn's Dialog
 *
 * @example
 * ```tsx
 * <Modal
 *   isOpen={open}
 *   onClose={close}
 *   title="Edit User"
 *   description="Make changes to user profile"
 *   size="md"
 *   footer={<Button onClick={save}>Save</Button>}
 * >
 *   <form>...</form>
 * </Modal>
 * ```
 */
export function Modal({
  isOpen,
  onClose,
  title,
  description,
  size = 'md',
  footer,
  children,
  className,
  bodyClassName = 'min-h-0 flex-1 overflow-y-auto py-4',
}: ModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={cn(sizeClasses[size], className)}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className={cn(bodyClassName)}>{children}</div>
        {footer && <DialogFooter className="flex-shrink-0">{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  )
}
